import Gameiom from "gameiom/gameiom";
import GameiomModel from "gameiom/model/gameiom-model";
import log from "gameiom/wrapper/logging";
import { Action, OperatorData, Ui, UiElements, WrapperMessage } from "gameiom/wrapper/wrapper-data";
import { Signal } from "signals";
import Components from "slotworks/components/components";
import gameLoop from "slotworks/core/game-loop";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import gameState from "slotworks/model/game-state";
import settingsModel from "slotworks/model/settings/settings-model";
import commsManager from "slotworks/server/comms-manager";
import { Services } from "slotworks/services/services";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import smartUiManager from "slotworks/smart-ui/smart-ui-manager";
import SoundManager from "slotworks/sound/sound-manager";
import DeviceInfo from "slotworks/utils/device-info";

class WrapperImplementation {

    public onResume: Signal = new Signal();

    private actionList: Map<Action, Function> = new Map<Action, Function>([
        [Action.enableAudio, SoundManager.unmute],
        [Action.disableAudio, SoundManager.mute],
        [Action.updateBalance, (data: any) => this.updateBalance(data.cash, data.bonus)],
        [Action.getOperatorInformation, (data: any) => this.configureForOperator(data)],
        [Action.balanceTooLow, (data: any) => this.showBalanceTooLowError(data)],
        [Action.isWrapperActive, (data: any) => this.updateWrapperActive(data.active)],
        [Action.getScreenSize, null],
        [Action.balancesHasChanged, (data: any) => this.updateBalance(data.cash, data.bonus)],
        [Action.pauseGame, () => this.onWrapperActivated()],
        [Action.resumeGame, () => this.onWrapperDeactivated()],
        [Action.wrapperActivityChanged, (data: any) => this.updateWrapperActive(data.active)],
        [Action.openAbout, () => this.openHelp()],
        [Action.openHelp, () => this.openHelp()],
        [Action.openPaytable, () => this.openPaytable()],
        [Action.openPreferences, () => this.openPreferences()],

        // These are just responses from the wrapper which we don't need to process
        [Action.gamePopupShown, null],
        [Action.gamePopupHidden, null],
        [Action.insufficientFunds, null],
        [Action.gameLoadingStarted, null],
        [Action.gameLoadingEnded, null],
        [Action.stakeUpdate, null],
        [Action.balancesUpdate, null],
        [Action.loadProgressUpdate, null],
        [Action.winningsUpdate, null],
        [Action.gameRoundEnded, null],
        [Action.gotoDepositPage, null],
        [Action.gotoAccountsPage, null],
        [Action.gameRoundStarted, null],
        [Action.exitGame, null]
    ] as any);

    public init(debug: boolean) {
        if (!gameWrapper) {
            throw new Error("Can't find window.gameWrapper. Has it been included?");
        }

        settingsModel.onSessionClose = () => this.wrapperShutdown();

        gameWrapper.onWrapperMessage = (action: string, message: WrapperMessage) => this.executeAction(action, message);

        gameWrapper.getOperatorInformation();
    }

    private onWrapperActivated() {
        log("Wrapper activated");
        SmartUIFlags.set(SmartUIFlag.AWAITING_RESPONSE, true);

        CanvasManager.setFullScreenPaused(true);
        gameLoop.setPaused("wrapper", true);
    }

    private onWrapperDeactivated() {
        log("Wrapper deactivated");
        SmartUIFlags.set(SmartUIFlag.AWAITING_RESPONSE, false);

        CanvasManager.setFullScreenPaused(false);
        gameLoop.setPaused("wrapper", false);

        this.onResume.dispatch();
    }

    private wrapperShutdown() {
        commsManager.connector.close();
    }

    private showBalanceTooLowError(data: any) {
        Components.alert.error("err.bal", false);
    }

    private executeAction(action: string, message: WrapperMessage) {

        const actionToPerform = this.actionList.get(action as Action);

        if (typeof actionToPerform === "undefined") {
            log(`Unknown action requested by wrapper: ${action}`);
            return;
        }

        if (actionToPerform === null) {
            return;
        }

        log(`Executing action: ${action}`);

        actionToPerform(message.data);
    }

    private configureForOperator(data: OperatorData) {
        if (!data) {
            log("Operator configure returned but no data found");
            return;
        }
        if (data.ui) {
            this.registerUiComponents(data.ui);
        }
        if (data.operator) {
            this.registerOperator(data.operator.id);
        }
        Gameiom.setupAgsControllerTypes();
    }

    /**
     * Values come in as currency, not "cents". E.g 100.10 instead of 10010
     */
    private updateBalance(cash: number, bonus: number) {
        const cashInCents = cash * 100;
        const bonusInCents = bonus * 100;

        Services.transaction.setBalance(cashInCents + bonusInCents);
        gameState.setWrapperOverrideBalance(cashInCents + bonusInCents);

        gameWrapper.balancesUpdateDecimal(cash, bonus);
    }

    private registerUiComponents(ui: Ui) {
        if (ui.displayClock) {
            GameiomModel.displayClock = ui.displayClock !== "disabled";
        }
        if (ui.maxBet) {
            GameiomModel.displayMaxBet = ui.maxBet !== "disabled";
        }
        if (ui.autoplay) {
            GameiomModel.autoplay = ui.autoplay;
        }
        if (ui.slamStop) {
            GameiomModel.disableSpinSkip = ui.slamStop === "disabled";
        }
        if (ui.denomDisplay) {
            GameiomModel.denomDisplay = ui.denomDisplay;
        }
        if (ui.disableFullscreen) {
            GameiomModel.disableFullscreen = ui.disableFullscreen;
        }
        if (ui.minStopDuration) {
            GameiomModel.minStopDuration = ui.minStopDuration;
        }
        if (ui.displayType) {
            GameiomModel.displayCurrency = ui.displayType === "currency";
        }
        if (ui.layouts) {
            if (DeviceInfo.isDesktop) {
                this.processElements(ui.layouts.desktop);
            } else {
                this.processElements(ui.layouts.mobile);
            }
        }
        if (ui.hideJackpotWording) {
            GameiomModel.hideJackpotWording = ui.hideJackpotWording !== "disabled";
        }
    }

    private processElements(elements: string[]) {
        this.hideAllElements();
        elements.forEach((element: string) => {
            this.processElement(element);
        });
    }

    private hideAllElements() {
        GameiomModel.displayButtons.forEach((value: boolean, element: UiElements) => {
            GameiomModel.displayButtons.set(element, false);
        });
    }

    private processElement(element: string) {
        const uiElement = element as UiElements;
        if (uiElement && GameiomModel.displayButtons.has(uiElement)) {
            GameiomModel.displayButtons.set(uiElement, true);
        } else {
            log("Trying to show UI element " + element + " but no ui element with that name was found");
        }
    }

    private registerOperator(id: number) {
        GameiomModel.operatorId = id;
    }

    private updateWrapperActive(active: boolean) {
        if (active) {
            this.onWrapperActivated();
        } else {
            this.onWrapperDeactivated();
        }
    }

    private openHelp() {
        this.openPaytable();

        if (!Layers.PaytableContent.currentSceneIs("default")) {
            let page = 1;
            while (Layers.PaytableContent.hasScene("page_" + page)) {
                page++;
            }
            Layers.PaytableContent.setScene("page_" + (page - 1)).execute();
        }
    }

    private openPaytable() {
        smartUiManager.showGroup("MenusContent_paytable", true).execute();
        smartUiManager.showGroup("MenusContent_paytable_rb", true).execute();
    }

    private openPreferences() {
        smartUiManager.showGroup("MenusContent_general", true).execute();
    }
}

export default new WrapperImplementation();
